import * as React from "react"

const SHOPIFY_API_VERSION = "2025-10"
const localStorageKey = `shopify_cart_id`
const isBrowser = typeof window !== `undefined`

const defaultValues = {
    cart: [],
    isOpen: false,
    loading: false,
    onOpen: () => {},
    onClose: () => {},
    addVariantToCart: () => {},
    removeLineItem: () => {},
    updateLineItem: () => {},
    client: null,
    checkout: {
        id: null,
        lineItems: [],
        totalPriceV2: {
            amount: "0",
            currencyCode: "JPY",
        },
        webUrl: "",
        checkoutUrl: "",
    },
}

export const StoreContext = React.createContext(defaultValues)

const getShopifyEndpoint = () => {
    const domain = process.env.GATSBY_SHOPIFY_STORE_URL
    return `https://${domain}/api/${SHOPIFY_API_VERSION}/graphql.json`
}

const shopifyFetch = async (query, variables = {}) => {
    const response = await fetch(getShopifyEndpoint(), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
            query,
            variables,
        }),
    })

    const json = await response.json()

    if (json.errors) {
        console.error("Shopify Storefront API errors:", json.errors)
        throw new Error(json.errors.map((error) => error.message).join(", "))
    }

    return json.data
}

const cartFragment = `
    id
    checkoutUrl
    cost {
        totalAmount {
            amount
            currencyCode
        }
    }
    lines(first: 100) {
        nodes {
            id
            quantity
            merchandise {
                ... on ProductVariant {
                    id
                    title
                    price {
                        amount
                        currencyCode
                    }
                    product {
                        title
                    }
                }
            }
        }
    }
`

const normalizeCart = (cart) => {
    if (!cart) {
        return defaultValues.checkout
    }

    return {
        id: cart.id,
        checkoutUrl: cart.checkoutUrl,
        webUrl: cart.checkoutUrl,
        totalPriceV2: cart.cost?.totalAmount || {
            amount: "0",
            currencyCode: "JPY",
        },
        lineItems:
            cart.lines?.nodes?.map((line) => ({
                id: line.id,
                quantity: line.quantity,
                title: line.merchandise?.product?.title || "",
                variant: {
                    id: line.merchandise?.id,
                    title: line.merchandise?.title || "",
                    priceV2: line.merchandise?.price || {
                        amount: "0",
                        currencyCode: "JPY",
                    },
                },
            })) || [],
    }
}

const createCart = async () => {
    const data = await shopifyFetch(
        `
        mutation cartCreate($input: CartInput) {
            cartCreate(input: $input) {
                cart {
                    ${cartFragment}
                }
                userErrors {
                    field
                    message
                }
            }
        }
        `,
        {
            input: {},
        }
    )

    const errors = data.cartCreate?.userErrors || []
    if (errors.length > 0) {
        throw new Error(errors.map((error) => error.message).join(", "))
    }

    return normalizeCart(data.cartCreate.cart)
}

const fetchCart = async (cartId) => {
    const data = await shopifyFetch(
        `
        query getCart($cartId: ID!) {
            cart(id: $cartId) {
                ${cartFragment}
            }
        }
        `,
        {
            cartId,
        }
    )

    return normalizeCart(data.cart)
}

const addCartLines = async (cartId, lines) => {
    const data = await shopifyFetch(
        `
        mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
                cart {
                    ${cartFragment}
                }
                userErrors {
                    field
                    message
                }
            }
        }
        `,
        {
            cartId,
            lines,
        }
    )

    const errors = data.cartLinesAdd?.userErrors || []
    if (errors.length > 0) {
        throw new Error(errors.map((error) => error.message).join(", "))
    }

    return normalizeCart(data.cartLinesAdd.cart)
}

const removeCartLines = async (cartId, lineIds) => {
    const data = await shopifyFetch(
        `
        mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
            cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
                cart {
                    ${cartFragment}
                }
                userErrors {
                    field
                    message
                }
            }
        }
        `,
        {
            cartId,
            lineIds,
        }
    )

    const errors = data.cartLinesRemove?.userErrors || []
    if (errors.length > 0) {
        throw new Error(errors.map((error) => error.message).join(", "))
    }

    return normalizeCart(data.cartLinesRemove.cart)
}

const updateCartLines = async (cartId, lines) => {
    const data = await shopifyFetch(
        `
        mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
                cart {
                    ${cartFragment}
                }
                userErrors {
                    field
                    message
                }
            }
        }
        `,
        {
            cartId,
            lines,
        }
    )

    const errors = data.cartLinesUpdate?.userErrors || []
    if (errors.length > 0) {
        throw new Error(errors.map((error) => error.message).join(", "))
    }

    return normalizeCart(data.cartLinesUpdate.cart)
}

export const StoreProvider = ({ children }) => {
    const [checkout, setCheckout] = React.useState(defaultValues.checkout)
    const [loading, setLoading] = React.useState(false)
    const [didJustAddToCart, setDidJustAddToCart] = React.useState(false)

    const setCheckoutItem = (checkoutItem) => {
        if (isBrowser && checkoutItem?.id) {
            localStorage.setItem(localStorageKey, checkoutItem.id)
        }

        setCheckout(checkoutItem || defaultValues.checkout)
    }

    React.useEffect(() => {
        const initializeCart = async () => {
            const existingCartID = isBrowser ? localStorage.getItem(localStorageKey) : null

            if (existingCartID && existingCartID !== `null`) {
                try {
                    const existingCart = await fetchCart(existingCartID)
                    if (existingCart?.id) {
                        setCheckoutItem(existingCart)
                        return
                    }
                } catch (e) {
                    console.error("Failed to fetch existing cart:", e)
                    localStorage.setItem(localStorageKey, null)
                }
            }

            try {
                const newCart = await createCart()
                setCheckoutItem(newCart)
            } catch (e) {
                console.error("Failed to create cart:", e)
            }
        }

        initializeCart()
    }, [])

    const ensureCart = async () => {
        if (checkout?.id) {
            return checkout
        }

        const newCart = await createCart()
        setCheckoutItem(newCart)
        return newCart
    }

    const addVariantToCart = async (variantId, quantity) => {
        setLoading(true)

        try {
            const currentCart = await ensureCart()

            const updatedCart = await addCartLines(currentCart.id, [
                {
                    merchandiseId: variantId,
                    quantity: parseInt(quantity, 10),
                },
            ])

            setCheckoutItem(updatedCart)
            setDidJustAddToCart(true)
            setTimeout(() => setDidJustAddToCart(false), 3000)

            return updatedCart
        } catch (e) {
            console.error("Failed to add item to cart:", e)
        } finally {
            setLoading(false)
        }
    }

    const removeLineItem = async (cartID, lineItemID) => {
        setLoading(true)

        try {
            const updatedCart = await removeCartLines(cartID, [lineItemID])
            setCheckoutItem(updatedCart)
            return updatedCart
        } catch (e) {
            console.error("Failed to remove item from cart:", e)
        } finally {
            setLoading(false)
        }
    }

    const updateLineItem = async (cartID, lineItemID, quantity) => {
        setLoading(true)

        try {
            const updatedCart = await updateCartLines(cartID, [
                {
                    id: lineItemID,
                    quantity: parseInt(quantity, 10),
                },
            ])

            setCheckoutItem(updatedCart)
            return updatedCart
        } catch (e) {
            console.error("Failed to update item quantity:", e)
        } finally {
            setLoading(false)
        }
    }

    return (
        <StoreContext.Provider
            value={{
                ...defaultValues,
                addVariantToCart,
                removeLineItem,
                updateLineItem,
                checkout,
                loading,
                didJustAddToCart,
            }}
        >
            {children}
        </StoreContext.Provider>
    )
}